import { useUser } from "@auth0/nextjs-auth0/client";
import { useBreakpoints } from "@common/hooks";
import { useHousehold } from "@common/queries";
import { Typography } from "@earned/core";
import cx from "classnames";
import * as React from "react";
import { HiX } from "react-icons/hi";

const DISPLAY_FREQUENCY_DAYS = 3;
const DISPLAY_FREQUENCY_MS = DISPLAY_FREQUENCY_DAYS * 24 * 60 * 60 * 1000;
const LAST_DISMISSED_KEY = "referralBannerLastDismissed";

const ReferralBanner: React.FC<{ isOpen: boolean; close: () => void }> = ({
  isOpen,
  close,
}) => {
  const [showReferralBanner, setShowReferralBanner] = React.useState(false);
  const [hasBeenDismissed, setHasBeenDismissed] = React.useState(false);
  const { isDesktop, isTablet } = useBreakpoints();

  const referralLink = React.useMemo(() => {
    return "https://www.earned.com/refer";
  }, []);

  const className = React.useMemo(
    () => cx("z-[80] h-16 w-full bg-green-1", { hidden: !isOpen }),
    [isOpen]
  );
  const { user } = useUser();
  const { data: household } = useHousehold({
    enabled: !!user,
  });

  React.useEffect(() => {
    const isLoaded = !!household;
    const isWealthDiagnosticClient =
      household?.currentProduct?.templateId === "wealthDiagnostic";
    const lastDismissed = household
      ? localStorage.getItem(`${LAST_DISMISSED_KEY}:${household.id}`)
      : null;
    const now = Date.now();
    const shouldShow =
      !lastDismissed ||
      now - parseInt(lastDismissed, 10) > DISPLAY_FREQUENCY_MS;
    if (
      isLoaded &&
      !isWealthDiagnosticClient &&
      !hasBeenDismissed &&
      shouldShow
    ) {
      setShowReferralBanner(true);
    }
  }, [household, setShowReferralBanner, hasBeenDismissed]);

  const handleDismiss = () => {
    setHasBeenDismissed(true);
    setShowReferralBanner(false);
    if (household) {
      localStorage.setItem(
        `${LAST_DISMISSED_KEY}:${household.id}`,
        Date.now().toString()
      );
    }
    close();
  };

  if (!showReferralBanner) return null;

  const isMobile = !isDesktop && !isTablet;

  return (
    <div
      id="referral-program"
      data-testid="referral-banner"
      className={className}
    >
      <div className="relative h-16">
        {/* Background color bars */}
        <div className="absolute inset-0 flex h-full w-full items-center">
          <div className="mr-5 h-11 w-1/2 bg-yellow-1"></div>
          <div className="ml-5 h-11 w-1/2 bg-blue-1"></div>
        </div>

        {/* SVG Background */}
        <div className="absolute inset-0 flex h-full items-center justify-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/referral-banner.svg"
            alt="Referral Banner"
            className="h-11 w-full object-none"
          />
        </div>

        {/* Content overlay */}
        <div className="relative z-10 grid h-full grid-cols-2 items-center">
          {/* Left section - Gold side with program name */}
          <div className="flex justify-start pl-3 sm:justify-center sm:pl-0">
            <Typography
              variant={isMobile ? "body-lg" : "title-lg"}
              color="tertiary"
              weight="medium"
              fontFamily="serif"
              component="span"
            >
              Earned Loyalty Program
            </Typography>
          </div>

          {/* Right section - Blue side with referral CTA */}
          <div
            className={cx("flex items-center pr-12 md:pr-16", {
              "justify-between pl-14 md:pl-16": !isMobile,
              "justify-end": isMobile,
            })}
          >
            {!isMobile && (
              <Typography
                variant="body"
                color="tertiary"
                weight="medium"
                component="span"
              >
                Earn Rewards by Referring Your Friends and Colleagues
              </Typography>
            )}
            <a href={referralLink} target="_blank" rel="noopener noreferrer">
              <Typography
                variant={isMobile ? "body-sm" : "body"}
                color="tertiary"
                className="underline"
              >
                Learn More
              </Typography>
            </a>
          </div>
        </div>

        {/* Close button */}
        <div className="absolute right-2 top-0 z-20 flex h-full flex-col justify-center md:right-7">
          <HiX
            id="close-referral"
            color="white"
            size={24}
            className="transition hover:cursor-pointer hover:opacity-80"
            onClick={handleDismiss}
          />
        </div>
      </div>
    </div>
  );
};

export default ReferralBanner;
